import Link from 'next/link'
import { useRouter } from 'next/router'
import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'

const EASE = [0.22, 1, 0.36, 1] as const

export interface RelatedItem {
  label: string
  description: string
  href: string
}

interface SolutionRelatedProps {
  eyebrow?: string
  title?: string
  items: RelatedItem[]
}

export default function SolutionRelated({
  eyebrow = 'Keep exploring',
  title = 'Related Monofor capabilities.',
  items,
}: SolutionRelatedProps) {
  const { locale } = useRouter()
  const learnMore = locale === 'tr' ? 'Daha fazla bilgi' : 'Learn more'
  if (!items.length) return null
  return (
    <Section tone="default" density="default">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow tone="accent">{eyebrow}</Eyebrow>
          <Heading as="h2" size="display-md" className="mt-5">
            {title}
          </Heading>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <motion.div
              key={item.href}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, ease: EASE, delay: (i % 3) * 0.06 }}
            >
              <Link
                href={item.href}
                className="group flex h-full flex-col justify-between rounded-2xl border border-line-subtle bg-surface p-6 transition duration-300 ease-smooth hover:-translate-y-0.5 hover:border-line hover:shadow-card-hover"
              >
                <div>
                  <div className="text-body-md font-medium text-ink-primary">
                    {item.label}
                  </div>
                  <div className="mt-2 text-body-sm text-ink-tertiary">
                    {item.description}
                  </div>
                </div>
                <div className="mt-6 inline-flex items-center gap-1.5 text-body-sm font-medium text-brand-900 transition group-hover:translate-x-0.5">
                  {learnMore}
                  <span aria-hidden>→</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  )
}
